<script lang="ts" setup>
import { computed, ref, watch } from "vue";
import ValorumLogo from "@/assets/images/brand/valorum-pro-logo.png";
import ValorumFavicon from "@/assets/images/brand/valorum-favicon.png";
import MenuComponents from "@/components/layouts/leftSideBar/verticalLayout/MenuComponents.vue";
import RailMenuComponent from "@/components/layouts/leftSideBar/verticalLayout/RailMenuComponent.vue";
import { useContextStore } from "@/stores/context.store";
import { useLayoutStore } from "@/store/app";
import {
  LAYOUTS,
  SIDEBAR_SIZE,
  LAYOUT_POSITION,
  LAYOUT_THEME,
  DIR,
} from "@/app/const";

const state = useLayoutStore();
const contextStore = useContextStore();

const mobileNavigationDrawer = ref(false);

const selectedProgram = computed(() => contextStore.selectedProgram);

const brandLogo = computed(() => {
  return selectedProgram.value?.logoUrl || ValorumLogo;
});

const brandAlt = computed(() => {
  return selectedProgram.value?.displayName || selectedProgram.value?.name || "Valorum";
});

const brandMark = computed(() => {
  return brandAlt.value.trim().charAt(0).toUpperCase() || "V";
});

const isCaremarkProgram = computed(() => {
  return selectedProgram.value?.logoUrl === "/brand/caremark-health-logo.png";
});

const isValorumProgram = computed(() => {
  return !selectedProgram.value || selectedProgram.value.clientName === "Valorum";
});

const sidebarThemeStyle = computed(() => ({
  "--caravel-nav": selectedProgram.value?.navColor || "#08263A",
  "--caravel-primary": selectedProgram.value?.primaryColor || "#009C9B",
  "--caravel-accent": selectedProgram.value?.accentColor || "#009C9B",
}));

const navigationDrawer = computed(() => {
  return state.layoutType === LAYOUTS.VERTICAL && !isSmallSideBar.value;
});

const isSmallSideBar = computed(() => {
  return state.sideBarSize === SIDEBAR_SIZE.SMALL;
});

const isCompactSideBar = computed(() => {
  return state.sideBarSize === SIDEBAR_SIZE.COMPACT;
});

const isScrollableLayout = computed(() => {
  return state.position === LAYOUT_POSITION.SCROLLABLE;
});

const sideBarSize = computed(() => {
  return state.sideBarSize;
});

const isRtl = computed(() => {
  return state.dir === DIR.RTL;
});

const verticalDrawerWidth = computed(() => {
  if (isCompactSideBar.value) {
    return 180;
  } else if (state.layoutTheme === LAYOUT_THEME.INTERACTION) {
    return 230;
  }

  return 312;
});

watch(sideBarSize, () => {
  mobileNavigationDrawer.value = !mobileNavigationDrawer.value;
});
</script>

<template>
  <v-navigation-drawer
    v-if="$vuetify.display.smAndUp"
    v-model="navigationDrawer"
    :width="verticalDrawerWidth"
    :absolute="isScrollableLayout"
    :permanent="$vuetify.display.smAndUp"
    :location="!isRtl ? 'start' : 'end'"
    temporary
    class="caravel-sidebar-drawer"
    :style="
      isScrollableLayout ? 'overflow-y: auto' : 'height: unset !important'
    "
  >
    <div class="caravel-sidebar" :style="sidebarThemeStyle">
      <div class="caravel-sidebar__top">
       <router-link
         to="/"
         class="caravel-sidebar__brand caravel-sidebar__brand--logo"
         :class="{
           'caravel-sidebar__brand--swell': selectedProgram?.clientName === 'Swell Medical',
           'caravel-sidebar__brand--caremark': isCaremarkProgram,
           'caravel-sidebar__brand--valorum': isValorumProgram,
         }"
       >
  <img
    :src="brandLogo"
    :alt="brandAlt"
    class="caravel-sidebar__logo"
  />
</router-link>

  
      </div>

      <div
        data-simplebar
        id="scrollbar"
        ref="scrollbar"
        class="caravel-sidebar__nav vertical-layout-sidebar"
      >
        <MenuComponents />
      </div>

      <div v-if="isValorumProgram" class="caravel-sidebar__legal">
        <a
          href="https://staging.valorumpro.com/copay-assistance-program-terms"
          target="_blank"
          rel="noopener noreferrer"
        >
          <span>Terms and Conditions</span>
          <i class="ph-arrow-square-out" aria-hidden="true" />
        </a>
      </div>

    </div>
  </v-navigation-drawer>

  <v-navigation-drawer
    v-else
    v-model="mobileNavigationDrawer"
    :width="312"
    temporary
    :location="!isRtl ? 'start' : 'end'"
    class="caravel-sidebar-drawer"
  >
    <div class="caravel-sidebar" :style="sidebarThemeStyle">
      <div class="caravel-sidebar__top">
        <router-link
          to="/"
          class="caravel-sidebar__brand caravel-sidebar__brand--logo"
          :class="{
            'caravel-sidebar__brand--swell': selectedProgram?.clientName === 'Swell Medical',
            'caravel-sidebar__brand--caremark': isCaremarkProgram,
            'caravel-sidebar__brand--valorum': isValorumProgram,
          }"
        >
  <img
    :src="brandLogo"
    :alt="brandAlt"
    class="caravel-sidebar__logo"
  />
</router-link>

        
      </div>

      <div
        data-simplebar
        id="scrollbar"
        ref="scrollbar"
        class="caravel-sidebar__nav mobile-navigation-drawer"
      >
        <MenuComponents />
      </div>

      <div v-if="isValorumProgram" class="caravel-sidebar__legal">
        <a
          href="https://staging.valorumpro.com/copay-assistance-program-terms"
          target="_blank"
          rel="noopener noreferrer"
        >
          <span>Terms and Conditions</span>
          <i class="ph-arrow-square-out" aria-hidden="true" />
        </a>
      </div>

    </div>
  </v-navigation-drawer>

  <v-navigation-drawer
    v-if="$vuetify.display.smAndUp && !navigationDrawer"
    v-model="isSmallSideBar"
    :rail-width="74"
    :location="isRtl ? 'end' : 'start'"
    rail
    permanent
    class="vertical-navigation-drawer-rail caravel-sidebar-rail"
    style="height: unset !important"
  >
    <div class="caravel-sidebar-rail__brand">
      <router-link to="/" class="caravel-sidebar__brand-mark" :style="sidebarThemeStyle">
        <img v-if="isValorumProgram" :src="ValorumFavicon" alt="ValorumPRO" />
        <template v-else>{{ brandMark }}</template>
      </router-link>
    </div>
    <v-divider />
    <div
      data-simplebar
      id="rail-scrollbar"
      :style="!isScrollableLayout ? 'height: calc(100vh - 100px)' : ''"
    >
      <RailMenuComponent />
    </div>
  </v-navigation-drawer>
</template>
